import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useLanguage } from "../contexts/LanguageContext";
import {
  ChevronLeft, ChevronRight, CalendarDays, CheckCircle,
  Clock, X, Phone, Mail, User, MessageSquare, ExternalLink
} from "lucide-react";

import { API } from "../config";

const WEEKDAYS = {
  lv: ["P", "O", "T", "C", "Pk", "S", "Sv"],
  ru: ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"],
  en: ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"],
};
const MONTH_NAMES = {
  lv: ["Janvāris","Februāris","Marts","Aprīlis","Maijs","Jūnijs","Jūlijs","Augusts","Septembris","Oktobris","Novembris","Decembris"],
  ru: ["Январь","Февраль","Март","Апрель","Май","Июнь","Июль","Август","Сентябрь","Октябрь","Ноябрь","Декабрь"],
  en: ["January","February","March","April","May","June","July","August","September","October","November","December"],
};

function getCalendarDays(year, month) {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startOffset = firstDay === 0 ? 6 : firstDay - 1;
  const days = [];
  for (let i = 0; i < startOffset; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);
  return days;
}

/* ---- Time Slot Picker (shown after clicking a day) ---- */
function TimeSlotsPanel({ date, lang, t, onSelectSlot, onClose }) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/calendar/day-slots`, { params: { date } })
      .then(r => setSlots(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoading(false));
  }, [date]);

  const statusStyle = {
    available: "bg-green-50 border-green-300 hover:bg-green-100 cursor-pointer text-green-800",
    booked: "bg-red-50 border-red-200 text-red-400 cursor-not-allowed",
    busy: "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed",
    past: "bg-gray-50 border-gray-100 text-gray-300 cursor-not-allowed",
  };

  const statusLabel = {
    available: t("calendar.available"),
    booked: lang === "ru" ? "Занято" : lang === "en" ? "Booked" : "Aizņemts",
    busy: t("calendar.busy"),
    past: t("calendar.past"),
  };

  return (
    <div data-testid="time-slots-panel"
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 px-4"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white border-2 border-black w-full max-w-md" style={{ boxShadow: "6px 6px 0 #FF6600" }}>
        <div className="flex items-center justify-between p-4 border-b-2 border-black bg-[#FF6600]">
          <div>
            <h3 className="font-black text-lg uppercase tracking-tight text-black" style={{ fontFamily: "Chivo, sans-serif" }}>
              {date}
            </h3>
            <p className="text-xs text-black/70">{lang === "ru" ? "Выберите время" : lang === "en" ? "Choose a time" : "Izvēlieties laiku"}</p>
          </div>
          <button onClick={onClose} className="text-black hover:text-white transition-colors"><X size={22} /></button>
        </div>
        <div className="p-4">
          {loading ? (
            <div className="py-8 text-center"><Clock size={24} className="animate-spin mx-auto text-[#FF6600]" /></div>
          ) : slots.length === 0 ? (
            <p className="text-center text-gray-400 py-8">{t("calendar.busy")}</p>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {slots.map(s => (
                <button key={s.time} data-testid={`time-slot-${s.time.replace(":", "")}`}
                  disabled={s.status !== "available"}
                  onClick={() => s.status === "available" && onSelectSlot(s.time)}
                  className={`border-2 p-3 text-center transition-all ${statusStyle[s.status] || statusStyle.busy}`}>
                  <span className="font-black text-lg block" style={{ fontFamily: "Chivo, sans-serif" }}>
                    {s.time}
                  </span>
                  <span className="text-xs block mt-0.5">{statusLabel[s.status] || s.status}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---- Booking Form Modal ---- */
function BookingModal({ date, time, lang, t, onClose, onSuccess }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", booking_type: "consultation", message: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/calendar/book`, { ...form, date, time });
      setResult(r.data);
    } catch (err) {
      setResult({ error: err?.response?.data?.detail || t("common.error") });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="booking-modal"
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/70 px-4"
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white border-2 border-black w-full max-w-lg" style={{ boxShadow: "6px 6px 0 #FF6600" }}>
        <div className="flex items-center justify-between p-5 border-b-2 border-black bg-[#FF6600]">
          <div>
            <h2 className="text-xl font-black uppercase tracking-tight text-black" style={{ fontFamily: "Chivo, sans-serif" }}>
              {t("calendar.bookTitle")}
            </h2>
            <p className="text-black/70 text-xs font-semibold mt-1">{date} &middot; {time} - {parseInt(time)+1}:00</p>
          </div>
          <button data-testid="booking-modal-close" onClick={onClose} className="text-black hover:text-white transition-colors"><X size={22} /></button>
        </div>

        {result && !result.error ? (
          <div className="p-8 text-center">
            <CheckCircle size={48} className="mx-auto mb-4 text-green-600" />
            <p className="font-black text-lg mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>{t("calendar.success")}</p>
            {result.google_calendar_url && (
              <a href={result.google_calendar_url} target="_blank" rel="noopener noreferrer"
                data-testid="google-calendar-link"
                className="rmd-btn-primary inline-flex items-center gap-2 text-xs">
                <CalendarDays size={16} />
                {lang === "ru" ? "Добавить в Google Календарь" : lang === "en" ? "Add to Google Calendar" : "Pievienot Google kalendāram"}
                <ExternalLink size={12} />
              </a>
            )}
            <button onClick={() => { onSuccess(); }} className="rmd-btn-secondary mt-3 w-full justify-center text-xs">
              {t("common.close")}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="rmd-label block mb-1">{t("calendar.name")} *</label>
                <div className="relative">
                  <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input required data-testid="booking-name" value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full border-2 border-black pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#FF6600]" />
                </div>
              </div>
              <div>
                <label className="rmd-label block mb-1">{t("calendar.email")} *</label>
                <div className="relative">
                  <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input required type="email" data-testid="booking-email" value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    className="w-full border-2 border-black pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#FF6600]" />
                </div>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="rmd-label block mb-1">{t("calendar.phone")}</label>
                <div className="relative">
                  <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input data-testid="booking-phone" value={form.phone}
                    onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="+371 XXXXXXXX"
                    className="w-full border-2 border-black pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#FF6600]" />
                </div>
              </div>
              <div>
                <label className="rmd-label block mb-1">{t("calendar.type")} *</label>
                <select data-testid="booking-type" value={form.booking_type}
                  onChange={e => setForm(f => ({ ...f, booking_type: e.target.value }))}
                  className="w-full border-2 border-black px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600] bg-white">
                  <option value="consultation">{t("calendar.consultation")}</option>
                  <option value="work">{t("calendar.work")}</option>
                </select>
              </div>
            </div>
            <div>
              <label className="rmd-label block mb-1">{t("calendar.message")}</label>
              <div className="relative">
                <MessageSquare size={14} className="absolute left-3 top-3 text-gray-400" />
                <textarea data-testid="booking-message" rows={3} value={form.message}
                  onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                  placeholder={t("calendar.messagePlaceholder")}
                  className="w-full border-2 border-black pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#FF6600] resize-none" />
              </div>
            </div>
            {result?.error && (
              <div data-testid="booking-error" className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2">{result.error}</div>
            )}
            <button data-testid="booking-submit" type="submit" disabled={loading}
              className="rmd-btn-primary w-full justify-center disabled:opacity-50">
              {loading ? "..." : t("calendar.submit")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

/* ---- Main Calendar Page ---- */
export default function CalendarPage() {
  const { t, lang } = useLanguage();
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [slots, setSlots] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);

  const fetchSlots = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/calendar/slots`, { params: { year, month: month + 1 } });
      const map = {};
      (r.data.slots || []).forEach(s => { map[s.date] = s; });
      setSlots(map);
    } catch { setSlots({}); }
    finally { setLoading(false); }
  }, [year, month]);

  useEffect(() => { fetchSlots(); }, [fetchSlots]);

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); };

  const days = getCalendarDays(year, month);
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

  const getSlotInfo = (day) => {
    if (!day) return null;
    const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return slots[dateStr] || { date: dateStr, status: "available", available_count: 8 };
  };
  const isPast = (day) => {
    if (!day) return false;
    return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}` < todayStr;
  };

  const statusColors = {
    available: { bg: "bg-green-50 hover:bg-green-100 border-green-300", text: "text-green-800", dot: "bg-green-500" },
    busy: { bg: "bg-red-50 border-red-200", text: "text-red-400", dot: "bg-red-500" },
    full: { bg: "bg-orange-50 border-orange-200", text: "text-orange-400", dot: "bg-orange-500" },
    weekend: { bg: "bg-gray-50 border-gray-200", text: "text-gray-300", dot: "bg-gray-300" },
    past: { bg: "bg-gray-50 border-gray-100", text: "text-gray-300", dot: "bg-gray-200" },
  };
  const canPrev = !(year === today.getFullYear() && month <= today.getMonth());

  return (
    <div style={{ paddingTop: 72 }} className="min-h-screen bg-[#f9f9f9]">
      <section className="bg-black py-12 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <span className="rmd-label text-[#FF6600] block mb-2">{t("calendar.title")}</span>
          <h1 className="text-white text-4xl md:text-6xl font-black uppercase tracking-tight leading-none" style={{ fontFamily: "Chivo, sans-serif" }} data-testid="calendar-title">
            {t("calendar.title")}
          </h1>
          <p className="text-white/50 mt-3 text-sm">{t("calendar.subtitle")}</p>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 md:px-12 py-8">
        <div className="flex flex-wrap gap-4 mb-6">
          {[{ key: "available", color: "bg-green-500" }, { key: "busy", color: "bg-red-500" }, { key: "full", color: "bg-orange-500" }, { key: "weekend", color: "bg-gray-300" }].map(({ key, color }) => (
            <div key={key} className="flex items-center gap-2 text-sm text-gray-600">
              <div className={`w-3 h-3 ${color}`} />{t(`calendar.${key}`)}
            </div>
          ))}
        </div>

        <div className="bg-white border-2 border-black" style={{ boxShadow: "4px 4px 0 #000" }}>
          <div className="flex items-center justify-between px-6 py-4 border-b-2 border-black bg-[#FF6600]">
            <button data-testid="prev-month-btn" onClick={prevMonth} disabled={!canPrev}
              className="p-2 text-black hover:bg-black/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
              <ChevronLeft size={24} />
            </button>
            <h2 className="font-black text-xl uppercase tracking-tight text-black" style={{ fontFamily: "Chivo, sans-serif" }} data-testid="calendar-month-label">
              {(MONTH_NAMES[lang] || MONTH_NAMES.lv)[month]} {year}
            </h2>
            <button data-testid="next-month-btn" onClick={nextMonth} className="p-2 text-black hover:bg-black/10 transition-colors">
              <ChevronRight size={24} />
            </button>
          </div>

          <div className="grid grid-cols-7 border-b border-gray-200">
            {(WEEKDAYS[lang] || WEEKDAYS.lv).map((d, i) => (
              <div key={i} className={`text-center py-2 text-xs font-bold uppercase tracking-wider ${i >= 5 ? "text-gray-300" : "text-gray-600"}`} style={{ fontFamily: "Chivo, sans-serif" }}>{d}</div>
            ))}
          </div>

          <div className="grid grid-cols-7" data-testid="calendar-grid">
            {loading ? (
              <div className="col-span-7 py-20 text-center text-gray-400">
                <Clock size={24} className="animate-spin mx-auto mb-2 text-[#FF6600]" />
                <p className="text-sm">{t("common.loading")}</p>
              </div>
            ) : days.map((day, i) => {
              if (!day) return <div key={`e-${i}`} className="aspect-square border border-gray-50" />;
              const slot = getSlotInfo(day);
              const past = isPast(day);
              const isToday = slot?.date === todayStr;
              const effectiveStatus = past ? "past" : slot?.status || "available";
              const colors = statusColors[effectiveStatus] || statusColors.available;
              const clickable = !past && effectiveStatus === "available";

              return (
                <button key={day} data-testid={`cal-day-${day}`} disabled={!clickable}
                  onClick={() => clickable && setSelectedDate(slot.date)}
                  className={`aspect-square border p-1 flex flex-col items-center justify-center transition-all relative ${colors.bg} ${clickable ? "cursor-pointer" : "cursor-default"} ${isToday ? "ring-2 ring-[#FF6600] ring-inset" : ""}`}>
                  <span className={`text-sm font-bold ${colors.text}`} style={{ fontFamily: "Chivo, sans-serif" }}>{day}</span>
                  {!past && effectiveStatus === "available" && (
                    <span className="text-[9px] text-green-600 font-semibold mt-0.5">{slot?.available_count || 0}h</span>
                  )}
                  {!past && effectiveStatus !== "weekend" && effectiveStatus !== "available" && (
                    <div className={`w-1.5 h-1.5 mt-1 ${colors.dot}`} />
                  )}
                  {isToday && <span className="absolute bottom-0.5 text-[8px] font-bold text-[#FF6600] uppercase">{t("calendar.today")}</span>}
                </button>
              );
            })}
          </div>
        </div>

        <div className="mt-6 p-4 border-l-4 border-[#FF6600] bg-white border border-gray-200">
          <div className="flex items-start gap-3">
            <CalendarDays size={20} className="text-[#FF6600] flex-shrink-0 mt-0.5" />
            <p className="text-sm text-gray-600">
              {lang === "ru" ? "Нажмите на зелёный день, чтобы выбрать время. Первая консультация — бесплатно."
                : lang === "en" ? "Click a green day to pick a time. First consultation is free."
                : "Noklikšķiniet uz zaļo dienu, lai izvēlētos laiku. Pirmā konsultācija – bezmaksas."}
            </p>
          </div>
        </div>
      </div>

      <footer className="bg-black py-8 mt-4">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <p className="text-white/30 text-xs">&copy; {new Date().getFullYear()} RMD – Real Men Do &middot; Cēsis, Latvija</p>
        </div>
      </footer>

      {/* Step 1: Pick time */}
      {selectedDate && !selectedTime && (
        <TimeSlotsPanel date={selectedDate} lang={lang} t={t}
          onSelectSlot={(time) => setSelectedTime(time)}
          onClose={() => setSelectedDate(null)} />
      )}
      {/* Step 2: Booking form */}
      {selectedDate && selectedTime && (
        <BookingModal date={selectedDate} time={selectedTime} lang={lang} t={t}
          onClose={() => { setSelectedDate(null); setSelectedTime(null); }}
          onSuccess={() => { setSelectedDate(null); setSelectedTime(null); fetchSlots(); }} />
      )}
    </div>
  );
}
